import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Maintenance } from '@/hooks/useMaintenances';
import { useToast } from '@/hooks/use-toast';
import { Edit, Trash2, Calendar, Euro, List, Grid3x3, ChevronDown, ChevronRight } from 'lucide-react';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

interface VueCompacteMaintenancesProps {
  bateauId?: string;
  onEditMaintenance: (maintenance: Maintenance) => void;
  maintenances: Maintenance[];
  supprimerMaintenance: (maintenanceId: string) => Promise<any>;
  modifierMaintenance: (id: string, modifications: Partial<Maintenance>) => Promise<any>;
  refetch: () => Promise<void>;
  costFilterYear?: number;
}

const VueCompacteMaintenances = ({
  bateauId,
  onEditMaintenance,
  maintenances,
  supprimerMaintenance,
  modifierMaintenance,
  costFilterYear,
}: VueCompacteMaintenancesProps) => {
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<'grouped' | 'all'>('grouped');
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isTermineesOpen, setIsTermineesOpen] = useState(false);

  // Filtrer par bateau et éventuellement par coût/année
  const filteredMaintenances = useMemo(() => {
    let result = bateauId
      ? maintenances.filter(m => m.bateau_id === bateauId)
      : maintenances;
    
    if (costFilterYear) {
      result = result.filter(m => {
        const year = new Date(m.date_echeance).getFullYear();
        return year === costFilterYear && m.prix;
      });
    }
    
    return result;
  }, [maintenances, bateauId, costFilterYear]);

  const handleStatusChange = async (maintenanceId: string, newStatus: string) => {
    console.log('Changing status to:', newStatus, 'for maintenance:', maintenanceId);
    setIsUpdating(maintenanceId);
    
    try {
      const result = await modifierMaintenance(maintenanceId, { statut: newStatus });
      if (result?.error) {
        console.error('Error updating maintenance status:', result.error);
        toast({
          title: "Erreur",
          description: "Impossible de mettre à jour le statut de la maintenance.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Statut mis à jour",
          description: `Le statut a été changé vers "${newStatus}".`,
        });
      }
    } catch (error) {
      console.error('Error updating maintenance status:', error);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(null);
    }
  };

  const handleDelete = async (maintenanceId: string) => {
    setIsDeleting(maintenanceId);
    
    try {
      const result = await supprimerMaintenance(maintenanceId);
      if (result?.error) {
        console.error('Error deleting maintenance:', result.error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer la maintenance.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Maintenance supprimée avec succès.",
        });
      }
    } catch (error) {
      console.error('Error deleting maintenance:', error);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const getStatusColor = (statut: string, isOverdue: boolean = false) => {
    if (isOverdue && statut !== 'Terminée') {
      return 'bg-red-50 border-red-200';
    }
    switch (statut) {
      case 'Terminée':
        return 'bg-blue-50 border-blue-200';
      case 'En cours':
        return 'bg-yellow-50 border-yellow-200';
      case 'En attente':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getCategorieIcon = (categorie: string) => {
    switch (categorie) {
      case 'Moteur':
        return '⚙️';
      case 'Coque':
        return '🚢';
      case 'Électronique':
        return '📡';
      case 'Gréement':
        return '⛵';
      case 'Sécurité':
        return '🦺';
      default:
        return '🔧';
    }
  };

  // Fonction pour vérifier si une maintenance est en retard
  const isMaintenanceOverdue = (maintenance: Maintenance): boolean => {
    // Une maintenance terminée n'est JAMAIS en retard
    if (maintenance.statut === 'Terminée') {
      return false;
    }
    const today = startOfDay(new Date());
    const dueDate = startOfDay(parseISO(maintenance.date_echeance));
    return isBefore(dueDate, today);
  };

  // Organiser les maintenances par statut avec useMemo pour éviter les recalculs inutiles
  const { enRetard, enCours, enAttente, terminees } = useMemo(() => {
    // 1. Terminées - basé uniquement sur le statut
    const terminees = filteredMaintenances
      .filter(m => m.statut === 'Terminée')
      .sort((a, b) => new Date(b.date_echeance).getTime() - new Date(a.date_echeance).getTime());
    
    // 2. Non terminées
    const nonTerminees = filteredMaintenances.filter(m => m.statut !== 'Terminée');
    
    // 3. En retard (date échue et non terminée)
    const enRetard = nonTerminees
      .filter(m => isMaintenanceOverdue(m))
      .sort((a, b) => new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime());
    
    // 4. En cours (non en retard)
    const enCours = nonTerminees
      .filter(m => m.statut === 'En cours' && !isMaintenanceOverdue(m))
      .sort((a, b) => new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime());
    
    // 5. En attente (non en retard)
    const enAttente = nonTerminees
      .filter(m => m.statut === 'En attente' && !isMaintenanceOverdue(m))
      .sort((a, b) => new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime());

    return { enRetard, enCours, enAttente, terminees };
  }, [filteredMaintenances]);

  const renderMaintenance = (maintenance: Maintenance) => {
    const isOverdue = isMaintenanceOverdue(maintenance);
    const statusColor = getStatusColor(maintenance.statut, isOverdue);
    const isCurrentlyUpdating = isUpdating === maintenance.id;
    const isCurrentlyDeleting = isDeleting === maintenance.id;
    
    return (
      <Card key={maintenance.id} className={`${statusColor} border transition-all hover:shadow-md ${isCurrentlyUpdating || isCurrentlyDeleting ? 'opacity-50' : ''}`}>
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Header avec icône */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{getCategorieIcon(maintenance.categorie)}</span>
                {isOverdue && (
                  <Badge variant="destructive" className="text-xs py-0">
                    En retard
                  </Badge>
                )}
              </div>
            </div>

            {/* Titre */}
            <h3 className={`font-semibold text-sm leading-tight ${
              maintenance.statut === 'Terminée' ? 'line-through text-muted-foreground' : 'text-gray-900'
            }`}>
              {maintenance.titre}
            </h3>

            {/* Description */}
            {maintenance.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {maintenance.description}
              </p>
            )}

            {/* Informations */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-1">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                    {format(parseISO(maintenance.date_echeance), 'dd/MM/yyyy', { locale: fr })}
                  </span>
                </div>
                <Badge variant="outline" className="text-xs py-0">
                  {maintenance.categorie}
                </Badge>
              </div>

              {maintenance.prix && (
                <div className="flex items-center space-x-1 text-xs">
                  <Euro className="h-3 w-3 text-muted-foreground" />
                  <span>{maintenance.prix}€</span>
                </div>
              )}

              {/* Récurrence */}
              {maintenance.recurrence && maintenance.recurrence !== 'Aucune' && (
                <div className="flex items-center space-x-1 text-xs">
                  <span className="text-muted-foreground">Récurrence:</span>
                  <Badge variant="outline" className="text-xs py-0">
                    {maintenance.recurrence}
                  </Badge>
                </div>
              )}

              {/* Sélecteur de statut et actions */}
              <div className="flex items-center justify-between pt-1">
                <Select
                  value={maintenance.statut}
                  onValueChange={(value) => handleStatusChange(maintenance.id, value)}
                  disabled={isCurrentlyUpdating || isCurrentlyDeleting}
                >
                  <SelectTrigger className="w-32 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="En attente">En attente</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="Terminée">Terminée</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="flex items-center space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onEditMaintenance(maintenance)}
                    className="h-7 w-7 p-0 text-nautic hover:text-nautic-light hover:bg-nautic/10"
                    disabled={isCurrentlyUpdating || isCurrentlyDeleting}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(maintenance.id)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={isCurrentlyUpdating || isCurrentlyDeleting}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (filteredMaintenances.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4 text-5xl">🔧</div>
        <h3 className="font-semibold text-lg mb-2">Aucune maintenance</h3>
        <p className="text-muted-foreground text-sm">
          Commencez par ajouter des tâches de maintenance pour vos navires.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Boutons de vue */}
      <div className="flex items-center gap-2">
        <Button
          variant={viewMode === 'grouped' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('grouped')}
          className="flex items-center gap-2"
        >
          <Grid3x3 className="h-4 w-4" />
          Par statut
        </Button>
        <Button
          variant={viewMode === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('all')}
          className="flex items-center gap-2"
        >
          <List className="h-4 w-4" />
          Toutes les maintenances
        </Button>
      </div>

      {viewMode === 'all' ? (
        /* Vue liste complète */
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Toutes les maintenances ({filteredMaintenances.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMaintenances
              .sort((a, b) => {
                return new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime();
              })
              .map(renderMaintenance)}
          </div>
        </div>
      ) : (
        /* Vue groupée par statut */
        <>
          {/* En retard */}
          {enRetard.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-red-600 mb-3 flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-red-500 rounded-full"></span>
                En retard ({enRetard.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enRetard.map(renderMaintenance)}
              </div>
            </div>
          )}

          {/* En cours */}
          {enCours.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-yellow-600 mb-3 flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-yellow-500 rounded-full"></span>
                En cours ({enCours.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enCours.map(renderMaintenance)}
              </div>
            </div>
          )}

          {/* En attente */}
          {enAttente.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-green-600 mb-3 flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
                En attente ({enAttente.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enAttente.map(renderMaintenance)}
              </div>
            </div>
          )}

          {/* Terminées - Menu dépliable */}
          {terminees.length > 0 && (
            <Collapsible open={isTermineesOpen} onOpenChange={setIsTermineesOpen}>
              <CollapsibleTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start p-0 h-auto hover:bg-transparent"
                >
                  <h3 className="text-lg font-semibold text-blue-600 flex items-center gap-2">
                    {isTermineesOpen ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronRight className="h-5 w-5" />
                    )}
                    <span className="inline-block w-3 h-3 bg-blue-500 rounded-full"></span>
                    Terminées ({terminees.length})
                  </h3>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {terminees.map(renderMaintenance)}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}
    </div>
  );
};

export default VueCompacteMaintenances;
